import type { zhCN } from './zh-CN.ts';

/**
 * English dictionary — parity-guaranteed mirror of the canonical zh-CN
 * dictionary. The `Record<keyof typeof zhCN, string>` annotation forces
 * every key present in `zh-CN.ts` to exist here; adding a key to the
 * canonical side without adding it here fails `pnpm check`.
 *
 * Conventions mirror `zh-CN.ts`:
 * - Placeholder syntax is `{name}`, substituted by `format.ts`.
 * - Trailing whitespace is never meaningful; punctuation follows normal
 *   English spacing rules (one space after periods, none before colons).
 * - Brand names + vendor / protocol / SQL identifiers stay in the JSX
 *   where they are referenced — they are not translatable content.
 */
export const en: Record<keyof typeof zhCN, string> = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nadoutong Ops Console',
  'meta.description':
    'Operate the Nadoutong router: upstreams, model pricing, gift cards, accounts and audit, revenue and payment reconciliation.',

  // ── Common / shared ─────────────────────────────────────────────
  'common.retry': 'Retry',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.delete': 'Delete',
  'common.deleting': 'Deleting…',
  'common.loading': 'Loading…',
  'common.network_error': 'Network error',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.never': 'Never',
  'common.copied': 'Copied to clipboard',
  'common.click_to_copy': 'Click to copy',
  'common.copy_anywhere': 'Click anywhere on this block to copy',

  'i18n.switcher.aria_label': 'Language',

  // ── Auth guard ──────────────────────────────────────────────────
  'auth.checking': 'Checking session…',

  // ── Login ───────────────────────────────────────────────────────
  'login.brand': 'Ops Console',
  'login.subtitle': 'Operator Sign-In',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign In',
  'login.submitting': 'Signing In…',
  'login.error.empty_fields': 'Email and password are required',

  // ── Shell chrome ────────────────────────────────────────────────
  'shell.brand.title': 'Ops Console',
  'shell.brand.subtitle': 'Nadoutong · Station α-7',
  'shell.nav.aria_label': 'Primary',
  'shell.group.overview': 'Overview',
  'shell.group.customers': 'Customers',
  'shell.group.platform': 'Platform',
  'shell.group.finance': 'Finance',
  'shell.group.support': 'Support',
  'shell.group.compliance': 'Compliance',
  'shell.group.system': 'System',
  'shell.group.supply': 'Supply & routing',
  'shell.group.governance': 'Governance & system',
  'shell.nav.overview': 'Business overview',
  'shell.nav.route_health': 'Route health',
  'shell.nav.customer_usage': 'Usage overview',
  'shell.nav.request_logs': 'Request logs',
  'shell.nav.dashboard': 'Dashboard',
  'shell.nav.upstreams': 'Upstreams',
  'shell.nav.models': 'Models',
  'shell.nav.gift_cards': 'Gift Cards',
  'shell.nav.accounts': 'Accounts',
  'shell.nav.failed_deductions': 'Failed Deductions',
  'shell.nav.billing_anomalies': 'Billing Anomalies',
  'shell.nav.payments_orders': 'Payment Orders',
  'shell.nav.payment_providers': 'Payment Providers',
  'shell.nav.pricing': 'Pricing',
  'shell.nav.fx_policy': 'FX policy',
  'shell.nav.channels': 'Channels',
  'shell.nav.revenue': 'Revenue',
  'shell.nav.reports': 'Reports',
  'shell.nav.webhook_events': 'Webhook events',
  'shell.nav.cron_runs': 'Cron runs',
  'shell.nav.ops_users': 'Ops users',
  'shell.nav.dr_check': 'DR drill',
  'shell.nav.support_requests': 'Support requests',
  'shell.nav.audit': 'Audit',
  'shell.system_status': 'System Status · Active',
  'shell.signed_in_fallback': 'signed in',
  'shell.role.fallback': 'OPS-ADMIN',
  'shell.logout': 'Log out',
  'shell.header.search.placeholder': 'Search records…',
  'shell.header.search.aria_label': 'Search',
  'shell.header.notifications.aria_label': 'Notifications',
  'shell.page.console_fallback': 'Ops Console',

  // ── Not found ───────────────────────────────────────────────────
  'notfound.title': 'Page not found',
  'notfound.body_prefix': 'The route you requested does not exist yet.',
  'notfound.back': 'Back to dashboard',

  // ── Dashboard (Mission Control) ─────────────────────────────────
  'dashboard.title': 'Mission Control',
  'dashboard.subtitle': 'Router traffic grouped by upstream',
  'dashboard.window.label': 'Window',
  'dashboard.window.7d': 'Last 7 days',
  'dashboard.window.30d': 'Last 30 days',
  'dashboard.window.90d': 'Last 90 days',
  'dashboard.stat.upstreams.label': 'Active Upstreams',
  'dashboard.stat.upstreams.delta': 'Seen in last {days} days',
  'dashboard.stat.requests.label': 'Total Requests',
  'dashboard.stat.requests.delta': 'Sum across {days}-day window',
  'dashboard.stat.failure_rate.label': 'Failure Rate',
  'dashboard.stat.failure_rate.delta': 'HTTP 4xx/5xx / total',
  'dashboard.stat.cost.label': 'Spend',
  'dashboard.stat.cost.delta': 'Summed gateway cost',
  'dashboard.loading_aria': 'Loading router stats…',
  'dashboard.error.title': 'Failed to load router stats',
  'dashboard.empty.title': 'No traffic in this window',
  'dashboard.empty.body_prefix': 'No rows in ',
  'dashboard.empty.body_suffix':
    ' landed in the last {days} days. Widen the window or check that the gateway is receiving requests.',
  'dashboard.column.upstream': 'Upstream',
  'dashboard.column.requests': 'Requests',
  'dashboard.column.failure_rate': 'Failure rate',
  'dashboard.column.latency': 'p95 (avg)',
  'dashboard.column.cost': 'Cost',
  'dashboard.row.deleted_upstream': 'deleted upstream',
  'dashboard.latency_note':
    'Latency column is currently an AVG approximation of p95 in the gateway (see router-stats contract).',

  // ── Dashboard proactive alerts (Epic #109 / P0.3) ───────────────
  'dashboard.alerts.title': 'Active alerts',
  'dashboard.alerts.subtitle': 'Signals that need operator attention right now',
  'dashboard.alerts.loading': 'Loading alerts…',
  'dashboard.alerts.error.title': 'Failed to load alerts',
  'dashboard.alerts.all_clear': 'No active alerts. All signals are green.',
  'dashboard.alerts.unrouted.title': 'Unrouted SKUs',
  'dashboard.alerts.unrouted.description':
    'Priced (vendor, model, protocol) triples with no enabled upstream. Tenant calls will fail.',
  'dashboard.alerts.unrouted.count': '{count} SKU(s)',
  'dashboard.alerts.single_point.title': 'Single-point SKUs',
  'dashboard.alerts.single_point.description':
    'SKUs with only one enabled upstream. A single outage takes them offline — add a second mapping.',
  'dashboard.alerts.single_point.count': '{count} SKU(s)',
  'dashboard.alerts.low_funding.title': 'Low-funding upstreams',
  'dashboard.alerts.low_funding.description':
    'Non-depleted batches with < 20% remaining capacity. Routing here will deplete soon.',
  'dashboard.alerts.low_funding.count': '{count} upstream(s)',
  'dashboard.alerts.low_funding.row': '{name} · {pct} remaining ({remaining} / {capacity})',
  'dashboard.alerts.low_funding.row_unknown': '{id} · {pct} remaining ({remaining} / {capacity})',
  'dashboard.alerts.unhealthy.title': 'Unhealthy (24h)',
  'dashboard.alerts.unhealthy.description':
    'Upstreams over the trailing 24h with ≥ 20 non-probe requests and > 20% 5xx failure rate.',
  'dashboard.alerts.unhealthy.count': '{count} upstream(s)',
  'dashboard.alerts.unhealthy.row': '{name} · {rate} failure ({failures} / {requests})',
  'dashboard.alerts.unhealthy.row_unknown': '{id} · {rate} failure ({failures} / {requests})',

  // ── Upstreams list ──────────────────────────────────────────────
  'upstreams.title': 'Upstreams',
  'upstreams.subtitle': 'Manage forward + pool + BYOK supply sources routed from gateway.',
  'upstreams.search.placeholder': 'Search name, endpoint…',
  'upstreams.search.aria_label': 'Search upstreams',
  'upstreams.new': 'New Upstream',
  'upstreams.new.title':
    'Launch the 4-step onboarding wizard: basics → model routes → register funding → probe',
  'upstreams.error.title': 'Failed to load upstreams',
  'upstreams.loading_aria': 'Loading upstreams…',
  'upstreams.empty.title': 'No upstreams configured',
  'upstreams.empty.search_body':
    'No upstreams match this search. Clear the filter or create a new one.',
  'upstreams.empty.initial_body':
    'Create your first upstream via the admin bootstrap flow (gateway /internal/upstreams) to start routing traffic.',
  'upstreams.stats.total': 'Total',
  'upstreams.stats.enabled': 'Enabled',
  'upstreams.stats.accounts': 'Accounts',
  'upstreams.stats.funding': 'Active Funding',
  'upstreams.column.name': 'Name',
  'upstreams.column.endpoint': 'Endpoint',
  'upstreams.column.source': 'Source',
  'upstreams.column.status': 'Status',
  'upstreams.column.accounts': 'Accounts',
  'upstreams.column.mappings': 'Mappings',
  'upstreams.column.funding': 'Funding',
  'upstreams.column.priority': 'Priority',
  'upstreams.column.actions': 'Actions',
  'upstreams.status.enabled': 'enabled',
  'upstreams.status.disabled': 'disabled',
  'upstreams.row.copy_endpoint': 'Copy endpoint URL',
  'upstreams.row.probe': 'Probe',
  'upstreams.row.edit': 'Edit',
  'upstreams.row.delete': 'Delete',
  'upstreams.delete_modal.title': 'Soft-delete upstream?',
  'upstreams.delete_modal.body_suffix':
    ' will be marked deleted (gateway stops routing to it). Reverse by editing `deleted_at` via a follow-up migration.',

  // ── Upstream detail ─────────────────────────────────────────────
  'upstream_detail.error.title': 'Failed to load upstream',
  'upstream_detail.back': 'Back to upstreams',
  'upstream_detail.breadcrumb.upstreams': 'Upstreams',
  'upstream_detail.meta.kind': 'kind · {kind}',
  'upstream_detail.meta.visibility': 'visibility · {visibility}',
  'upstream_detail.chip.enabled': 'enabled',
  'upstream_detail.chip.disabled': 'disabled',
  'upstream_detail.config.title': 'Configuration',
  'upstream_detail.config.display_name': 'Display name',
  'upstream_detail.config.endpoint_url': 'Endpoint URL',
  'upstream_detail.config.priority': 'Priority',
  'upstream_detail.config.enabled': 'Enabled',
  'upstream_detail.config.auth_secret': 'Stored secret',
  'upstream_detail.config.auth_secret.write_only': 'write-only secret present',
  'upstream_detail.config.auth_secret.empty': 'no secret stored',
  'upstream_detail.config.auth_secret.hint':
    'Gateway stores this secret encrypted at rest. Operators can rotate it, but cannot read it back.',
  'upstream_detail.config.cookie_passthrough': 'Cookie passthrough',
  'upstream_detail.config.cookie_passthrough.state.enabled':
    'Tenant cookies are forwarded to this upstream.',
  'upstream_detail.config.cookie_passthrough.state.disabled':
    'Tenant cookies are stripped before this upstream request leaves the gateway.',
  'upstream_detail.config.cookie_passthrough.hint':
    'Leave this off unless the upstream explicitly requires tenant cookies for auth or session continuity.',
  'upstream_detail.config.state.enabled': 'Upstream is active',
  'upstream_detail.config.state.disabled': 'Upstream is disabled',
  'upstream_detail.probe.title': 'Probe',
  'upstream_detail.probe.hint': 'Results persist for this session only',
  'upstream_detail.probe.vendor': 'Vendor',
  'upstream_detail.probe.vendor_placeholder': 'Select vendor',
  'upstream_detail.probe.channel': 'Channel',
  'upstream_detail.probe.channel_placeholder': 'Select channel',
  'upstream_detail.probe.model': 'Model',
  'upstream_detail.probe.select.placeholder': 'Select model',
  'upstream_detail.probe.submit': 'Probe {name}',
  'upstream_detail.probe.submitting': 'Probing…',
  'upstream_detail.probe.error.select_model': 'Select vendor, channel and model first',
  'upstream_detail.probe_history.title': 'Probe history',
  'upstream_detail.probe_history.body':
    'Persisted probe outcomes for this upstream. Auto-refreshes after each new probe.',
  'upstream_detail.probe_history.count': '{count} recent',
  'upstream_detail.probe_history.loading': 'Loading probe history…',
  'upstream_detail.probe_history.error': 'Failed to load probe history.',
  'upstream_detail.probe_history.empty': 'No probe results recorded yet.',
  'upstream_detail.probe_history.column.time': 'Time (UTC)',
  'upstream_detail.probe_history.column.model': 'Vendor / model',
  'upstream_detail.probe_history.column.outcome': 'Outcome',
  'upstream_detail.probe_history.column.latency': 'Latency',
  'upstream_detail.probe_history.column.cost': 'Cost (USD)',
  'upstream_detail.probe_history.column.status': 'HTTP',
  'upstream_detail.routing.title': 'Capability discovery & routing',
  'upstream_detail.routing.subtitle':
    'Confirm which upstream models really work, then bind passed capabilities into tenant routes.',
  'upstream_detail.routing.protocol': 'Probe protocol',
  'upstream_detail.routing.discover': 'Fetch model list',
  'upstream_detail.routing.discovering': 'Fetching models…',
  'upstream_detail.routing.toast.models_loaded': 'Model list fetched: {count} candidate(s)',
  'upstream_detail.routing.toast.capability_probe_queued':
    'Single-model CLI probe job queued; waiting for result',
  'upstream_detail.routing.toast.capability_ignored': 'Candidate ignored',
  'upstream_detail.routing.toast.capability_unignored':
    'Candidate unignored; probe it again before binding',
  'upstream_detail.routing.capabilities.title': 'Verified upstream capabilities',
  'upstream_detail.routing.capabilities.subtitle':
    'This table is not tenant routing. It shows upstream candidates found from /v1/models for the selected channel and vendor. Probe individual models with CLI smoke checks; only passed rows become hot-path candidates after route binding.',
  'upstream_detail.routing.capabilities.empty': 'No candidates yet. Fetch the model list first.',
  'upstream_detail.routing.capabilities.empty_channel':
    'No discovery results for the selected channel.',
  'upstream_detail.routing.capabilities.empty_routable':
    'No bindable candidates for this channel. Switch to all records to inspect failed or pending probes.',
  'upstream_detail.routing.capabilities.empty_filter': 'No records match this filter.',
  'upstream_detail.routing.capabilities.filter.channel': 'Capability channel',
  'upstream_detail.routing.capabilities.filter.channel.empty': 'No channels',
  'upstream_detail.routing.capabilities.filter.vendor': 'Vendor',
  'upstream_detail.routing.capabilities.filter.vendor.empty': 'No vendors',
  'upstream_detail.routing.capabilities.filter.mode': 'Results',
  'upstream_detail.routing.capabilities.filter.mode.routable': 'Bindable',
  'upstream_detail.routing.capabilities.filter.mode.all': 'All records',
  'upstream_detail.routing.capabilities.summary.total': '{count} candidates',
  'upstream_detail.routing.capabilities.summary.bindable': '{count} bindable',
  'upstream_detail.routing.capabilities.summary.bound': '{count} bound',
  'upstream_detail.routing.capabilities.summary.failed': '{count} failed',
  'upstream_detail.routing.capabilities.summary.needs_probe': '{count} needs probe',
  'upstream_detail.routing.capabilities.summary.ignored': '{count} ignored',
  'upstream_detail.routing.capabilities.group.count': '{count} probe records',
  'upstream_detail.routing.capabilities.col.channel': 'Channel',
  'upstream_detail.routing.capabilities.col.model': 'Upstream candidate',
  'upstream_detail.routing.capabilities.col.status': 'CLI probe status',
  'upstream_detail.routing.capabilities.col.action': 'Route action',
  'upstream_detail.routing.capability.status.passed': 'Passed',
  'upstream_detail.routing.capability.status.passed.ready': 'Ready to bind into a route.',
  'upstream_detail.routing.capability.status.passed.hint': 'CLI passed; last passed: {time}',
  'upstream_detail.routing.capability.status.needs_probe': 'Needs probe',
  'upstream_detail.routing.capability.status.needs_probe.hint':
    'A successful probe is required before binding.',
  'upstream_detail.routing.capability.status.probe_failed': 'Probe failed',
  'upstream_detail.routing.capability.status.probe_failed.hint':
    'Check the key, channel binding, or model name.',
  'upstream_detail.routing.capability.status.missing_from_list': 'Missing from list',
  'upstream_detail.routing.capability.status.missing_from_list.hint':
    'The latest upstream model list did not include this model.',
  'upstream_detail.routing.capability.status.mismatch': 'Mismatch',
  'upstream_detail.routing.capability.status.mismatch.hint':
    'Current probe state does not match the existing capability record; review manually.',
  'upstream_detail.routing.capability.status.ignored': 'Ignored',
  'upstream_detail.routing.capability.status.ignored.hint':
    'This candidate is hidden from the default list. Unignore it and probe again before binding.',
  'upstream_detail.routing.capability.action.bound': 'Binding created',
  'upstream_detail.routing.capability.action.bind': 'Create route binding',
  'upstream_detail.routing.capability.action.needs_pass': 'Probe not passed',
  'upstream_detail.routing.capability.action.ignored': 'Ignored',
  'upstream_detail.routing.capability.action.ignore': 'Ignore',
  'upstream_detail.routing.capability.action.unignore': 'Unignore',
  'upstream_detail.routing.capability.action.probe': 'Start probe',
  'upstream_detail.routing.capability.action.reprobe': 'Re-probe',
  'upstream_detail.routing.capability.action.probing': 'Queueing…',
  'upstream_detail.routing.capability.action.waiting': 'Waiting for result…',
  'upstream_detail.routing.bindings.title': 'Tenant route bindings',
  'upstream_detail.routing.bindings.subtitle':
    'These are the tenant-routing candidates. Only enabled bindings enter the hot path; disabled bindings are retained as configuration.',
  'upstream_detail.routing.bindings.empty':
    'No route bindings yet. Create one from a passed capability above.',
  'upstream_detail.routing.bindings.col.channel': 'Channel',
  'upstream_detail.routing.bindings.col.model': 'Model',
  'upstream_detail.routing.bindings.col.enabled': 'Hot path',
  'upstream_detail.routing.bindings.col.action': 'Action',
  'upstream_detail.routing.bindings.enabled.yes': 'Enabled',
  'upstream_detail.routing.bindings.enabled.no': 'Disabled',
  'upstream_detail.routing.bindings.action.enable': 'Enable',
  'upstream_detail.routing.bindings.action.disable': 'Disable',
  'upstream_detail.routing.bindings.action.delete': 'Delete',
  'upstream_detail.routing.legacy_notice':
    'Legacy mappings are retained for migration visibility only and are no longer the new routing configuration entrypoint. New tenant routing uses capabilities and route bindings above.',
  'upstream_detail.funding.title': 'Funding batches',
  'upstream_detail.funding.new': '+ New batch',
  'upstream_detail.funding.loading_aria': 'Loading funding batches…',
  'upstream_detail.funding.error.load': 'Failed to load funding batches',
  'upstream_detail.funding.empty':
    'No funding batches recorded. Add one as needed to log current balance or prepaid quota.',
  'upstream_detail.funding.account.unassigned': 'Unassigned',
  'upstream_detail.funding.form.account.label': 'Account',
  'upstream_detail.funding.form.account.none': 'No accounts',
  'upstream_detail.funding.register.title': 'Register funding batch',
  'upstream_detail.funding.register.submit': 'Register batch',
  'upstream_detail.funding.register.submitting': 'Saving…',
  'upstream_detail.funding.batch.mode_rate': 'mode · {mode} · rate {rate}',
  'upstream_detail.funding.batch.chip.committed': 'committed',
  'upstream_detail.funding.batch.chip.draft': 'draft',
  'upstream_detail.funding.batch.chip.active': 'active',
  'upstream_detail.funding.batch.chip.depleted': 'depleted',
  'upstream_detail.funding.batch.chip.alert_95': 'alert 95%',
  'upstream_detail.funding.batch.chip.alert_80': 'alert 80%',
  'upstream_detail.funding.batch.meta.paid': 'Paid',
  'upstream_detail.funding.batch.meta.capacity': 'Capacity',
  'upstream_detail.funding.batch.meta.consumed': 'Consumed',
  'upstream_detail.funding.batch.meta.funded_at': 'Funded at',
  'upstream_detail.funding.batch.pct_consumed': '{pct}% consumed',
  'upstream_detail.funding.batch.invoice': 'invoice',
  'upstream_detail.funding.batch.note': 'note',
  'upstream_detail.funding.form.cny_paid_yuan.label': 'CNY paid (yuan)',
  'upstream_detail.funding.form.exchange_rate.label': 'Exchange rate (CNY/USD)',
  'upstream_detail.funding.form.exchange_rate.loading': 'Loading rate…',
  'upstream_detail.funding.form.computed_usd.label': 'Computed USD capacity',
  'upstream_detail.funding.form.invoice_ref.label': 'Invoice reference (optional)',
  'upstream_detail.funding.form.note.label': 'Note (optional)',
  'upstream_detail.funding.error.cny_paid_required': 'Please enter the CNY amount paid',
  'upstream_detail.funding.error.account_required': 'Please choose an upstream account',
  'upstream_detail.funding.error.rate_unavailable':
    'Exchange rate not available. Set it on the Pricing page first.',
  'upstream_detail.funding.toast.registered': 'Funding batch {id} registered',
  'upstream_detail.funding.toast.committed': 'Funding batch committed',
  'upstream_detail.funding.toast.updated': 'Funding batch updated',
  'upstream_detail.funding.batch.action.edit': 'Edit',
  'upstream_detail.funding.batch.action.commit': 'Commit',
  'upstream_detail.danger.title': 'Danger zone',
  'upstream_detail.danger.body':
    'Soft-delete the upstream. Gateway stops routing to it; model mappings stay in place. Reversible by a follow-up migration.',
  'upstream_detail.danger.already_deleted': 'Already soft-deleted',
  'upstream_detail.danger.delete': 'Soft-delete upstream',
  'upstream_detail.delete_modal.title': 'Soft-delete {name}?',
  'upstream_detail.delete_modal.body':
    'Gateway will stop routing to this upstream immediately. Model mappings stay in place.',

  // ── Upstream retirement impact dialog ───────────────────────────
  'upstream_detail.retirement.loading': 'Analyzing retirement impact…',
  'upstream_detail.retirement.load_error':
    'Could not load retirement impact. Retry or contact the platform admin.',
  'upstream_detail.retirement.retry': 'Retry',
  'upstream_detail.retirement.uniquely_served.title': '🚨 Uniquely served models',
  'upstream_detail.retirement.uniquely_served.body':
    'Retiring this upstream leaves {count} model(s) with no route. Tenant calls will 404 immediately:',
  'upstream_detail.retirement.uniquely_served.none':
    'No uniquely served models — every mapped model route has another upstream that can route it.',
  'upstream_detail.retirement.total_attached_suffix':
    '({total} mapped in total, {unique} served only by this upstream)',
  'upstream_detail.retirement.funding.title': '💰 Active funding batches',
  'upstream_detail.retirement.funding.body':
    '{count} batch(es) not yet depleted, {remaining} remaining. Retirement stops further consumption and breaks audit traceability.',
  'upstream_detail.retirement.funding.none': 'No active funding batches.',
  'upstream_detail.retirement.traffic.title': '📊 Traffic share — last 24 h',
  'upstream_detail.retirement.traffic.body':
    'Handled {percentage}% of gateway traffic in the last 24 h ({handled}/{total} requests).',
  'upstream_detail.retirement.traffic.no_traffic':
    'No traffic in the last 24 h. Share cannot be estimated.',
  'upstream_detail.retirement.ack':
    'I understand the consequences of retiring this upstream and wish to proceed with the soft-delete.',
  'upstream_detail.retirement.ack_required': 'Please acknowledge the box above to continue.',

  // ── Models list ─────────────────────────────────────────────────
  'models.title': 'Models',
  'models.subtitle':
    'Catalog of registered (vendor, model) pairs. Pricing history and protocol bindings are managed on the per-model detail page.',
  'models.search.placeholder': 'Search model, display name…',
  'models.search.aria_label': 'Search models',
  'models.filter.vendor': 'Vendor',
  'models.filter.vendor_all': 'All vendors',
  'models.new': 'Discover Models',
  'models.error.title': 'Failed to load models',
  'models.loading_aria': 'Loading models…',
  'models.empty.search.title': 'No models match this search',
  'models.empty.search.body': 'Clear the filter, or discover models from the vendor page first.',
  'models.empty.initial.title': 'No models discovered',
  'models.empty.initial.body':
    'Run official discovery from the vendor page; discovered results will appear here as delisted candidates.',
  'models.column.vendor': 'Vendor',
  'models.column.model': 'Model',
  'models.column.display_name': 'Display name',
  'models.column.price': 'Price',
  'models.column.family': 'Family',
  'models.column.context_window': 'Context window',
  'models.column.actions': 'Actions',
  'models.price.unit': 'input / output · USD/MTok',
  'models.price.cache_line': 'cache read {read} / write {write}',
  'models.price.unpriced': 'Unpriced',
  'models.row.empty_value': '—',
  'models.row.manage': 'Manage',
  'models.row.delete': 'Delete',
  'models.delete_modal.title': 'Soft-delete model?',
  'models.delete_modal.body_suffix':
    ' will be marked deleted. Pricing and protocol rows are preserved for historical reconstruction; reverse by editing ',
  'models.delete_modal.body_deleted_at_code': 'deleted_at',
  'models.delete_modal.body_tail': ' via a follow-up migration.',

  // ── Model detail ────────────────────────────────────────────────
  'model_detail.loading_aria': 'Loading model…',
  'model_detail.error.title': 'Failed to load model',
  'model_detail.back': 'Back to models',
  'model_detail.breadcrumb.models': 'Models',
  'model_detail.meta.family': 'family · {family}',
  'model_detail.meta.soft_deleted': 'soft-deleted · {date}',
  'model_detail.meta.deprecated': 'deprecated · {date}',
  'model_detail.basic.title': 'Basic info',
  'model_detail.basic.display_name': 'Display name',
  'model_detail.basic.family': 'Family',
  'model_detail.basic.context_window': 'Context window',
  'model_detail.basic.max_output_tokens': 'Max output tokens',
  'model_detail.pricing.title': 'Pricing history',
  'model_detail.pricing.loading_aria': 'Loading pricing…',
  'model_detail.pricing.error.load': 'Network error loading pricing',
  'model_detail.pricing.empty': 'No pricing rows yet. Add the first generation below.',
  'model_detail.pricing.column.effective_from': 'Effective from',
  'model_detail.pricing.column.input': 'Input $/Mtok',
  'model_detail.pricing.column.output': 'Output $/Mtok',
  'model_detail.pricing.column.cache_read': 'Cache read $/Mtok',
  'model_detail.pricing.column.cache_write': 'Cache write $/Mtok',
  'model_detail.pricing.column.status': 'Status',
  'model_detail.pricing.row.current': 'Current',
  'model_detail.pricing.row.archived': 'archived',
  'model_detail.pricing.add.title': 'Add pricing row',
  'model_detail.pricing.add.effective_from.label': 'Effective from (date)',
  'model_detail.pricing.add.input_rate.label': 'Input $/Mtok',
  'model_detail.pricing.add.input_rate.placeholder': '3.000000',
  'model_detail.pricing.add.output_rate.label': 'Output $/Mtok',
  'model_detail.pricing.add.output_rate.placeholder': '15.000000',
  'model_detail.pricing.add.cache_read.label': 'Cache read $/Mtok',
  'model_detail.pricing.add.cache_read.placeholder': '0.300000',
  'model_detail.pricing.add.cache_write.label': 'Cache write $/Mtok',
  'model_detail.pricing.add.cache_write.placeholder': '3.750000',
  'model_detail.pricing.add.submit': 'Add pricing row',
  'model_detail.pricing.add.submitting': 'Adding…',
  'model_detail.pricing.error.effective_from_required': 'Effective from is required (ISO-8601 UTC)',
  'model_detail.pricing.error.effective_from_invalid': 'Effective from must be a valid date',
  'model_detail.pricing.error.rates_required': 'Input and output rates are required',
  'model_detail.pricing.error.input_rate_invalid': 'Input rate must be a non-negative number',
  'model_detail.pricing.error.output_rate_invalid': 'Output rate must be a non-negative number',
  'model_detail.pricing.error.cache_read_invalid': 'Cache read must be a non-negative number',
  'model_detail.pricing.error.cache_write_invalid': 'Cache write must be a non-negative number',
  'model_detail.pricing.error.invalid': 'Invalid rate',
  'model_detail.protocols.title': 'Protocols',
  'model_detail.protocols.loading_aria': 'Loading protocols…',
  'model_detail.protocols.error.load': 'Network error loading protocols',
  'model_detail.protocols.empty': 'No protocols registered. Pool selectors will skip this model.',
  'model_detail.protocols.add.label': 'Add protocol',
  'model_detail.protocols.add.submit': 'Add',
  'model_detail.protocols.add.adding': 'Adding…',
  'model_detail.protocols.error.already_registered': '{protocol} is already registered',
  'model_detail.protocols.remove_aria': 'Remove {protocol}',
  'model_detail.deprecation.title': 'Deprecation',
  'model_detail.deprecation.body':
    'Marking this model deprecated keeps it listable, chargeable, and routable — ops is just signalling new adoption should be discouraged. The notice surfaces in the detail header and the ops audit log.',
  'model_detail.deprecation.chip.deprecated': 'Deprecated',
  'model_detail.deprecation.notice.label': 'Notice (required)',
  'model_detail.deprecation.notice.placeholder':
    'e.g. Vendor EOL on 2026-06-01. Migrate tenants to claude-opus-4-8.',
  'model_detail.deprecation.notice.counter': '{count} / {limit}',
  'model_detail.deprecation.deprecate.submit': 'Mark deprecated',
  'model_detail.deprecation.deprecate.pending': 'Marking…',
  'model_detail.deprecation.undeprecate.submit': 'Clear deprecation',
  'model_detail.deprecation.undeprecate.pending': 'Clearing…',
  'model_detail.deprecation.error.notice_required': 'Enter a deprecation notice',
  'model_detail.deprecation.error.notice_too_long': 'Notice must be at most {limit} characters',
  'model_detail.deprecation.toast.deprecated': 'Model marked deprecated',
  'model_detail.deprecation.toast.undeprecated': 'Deprecation cleared',
  'model_detail.danger.title': 'Danger zone',
  'model_detail.danger.body':
    'Soft-delete the model. Pricing rows and protocol bindings stay in D1 for historical cost reconstruction; gateway pool selectors will stop offering this model.',
  'model_detail.danger.already_deleted': 'Already soft-deleted',
  'model_detail.danger.delete': 'Soft-delete model',
  'model_detail.delete_modal.title': 'Soft-delete {vendor}/{model}?',
  'model_detail.delete_modal.body':
    'Gateway pool selectors will stop offering this model immediately. Historical usage logs continue to resolve via the preserved pricing rows.',

  // ── Gift cards ──────────────────────────────────────────────────
  'gift_cards.title': 'Gift Cards',
  'gift_cards.subtitle':
    'Issue by batch, inspect redemption state, and keep tenant self-redeem plus ops redeem-on-behalf visible in one ledger.',
  'gift_cards.batch.unbatched': 'Unbatched',
  'gift_cards.batches.title': 'Batches',
  'gift_cards.batches.empty': 'No batches yet',
  'gift_cards.batches.status.active': 'active',
  'gift_cards.batches.status.closed': 'closed',
  'gift_cards.batches.close': 'Close',
  'gift_cards.batches.reopen': 'Reopen',
  'gift_cards.batches.metric.issued': 'Issued',
  'gift_cards.batches.metric.active': 'Active',
  'gift_cards.batches.metric.redeemed': 'Redeemed',
  'gift_cards.batches.metric.value': 'Face value',
  'gift_cards.batches.create.name': 'Batch name',
  'gift_cards.batches.create.description': 'Description (optional)',
  'gift_cards.batches.create.submit': 'Create batch',
  'gift_cards.batches.create.submitting': 'Creating…',
  'gift_cards.batches.error.name_required': 'Batch name is required',
  'gift_cards.chip.revoked': 'revoked',
  'gift_cards.chip.redeemed': 'redeemed',
  'gift_cards.chip.expired': 'expired',
  'gift_cards.chip.active': 'active',
  'gift_cards.issue.title': 'Issue',
  'gift_cards.issue.subtitle': 'Hot path · creates a credit voucher',
  'gift_cards.issue.amount.label': 'Amount (RMB)',
  'gift_cards.issue.amount.placeholder': '50',
  'gift_cards.issue.code_hint.label': 'Code hint (optional)',
  'gift_cards.issue.code_hint.placeholder': 'auto-generated 32-hex',
  'gift_cards.issue.batch_id.label': 'Batch id (optional)',
  'gift_cards.issue.batch_id.placeholder': '2026-q2-promo',
  'gift_cards.issue.expires_at.label': 'Expires at (optional)',
  'gift_cards.issue.submit': 'Issue gift card',
  'gift_cards.issue.submitting': 'Issuing…',
  'gift_cards.issued.title': 'New gift card issued',
  'gift_cards.issued.code_label': 'Code (click to copy)',
  'gift_cards.issued.code_aria_label': 'Copy gift card code',
  'gift_cards.issued.copy_status.copied': 'Copied to clipboard',
  'gift_cards.issued.copy_status.idle': 'Click anywhere on this block to copy',
  'gift_cards.issued.face_value': 'Face value',
  'gift_cards.issued.batch': 'Batch',
  'gift_cards.issued.expires_at': 'Expires at',
  'gift_cards.issued.never': 'Never',
  'gift_cards.issued.credit_value': 'USD credit',
  'gift_cards.issued.applied_rate': 'Locked rate',
  'gift_cards.issued.issue_another': 'Issue another',
  'gift_cards.lookup.title': 'Lookup',
  'gift_cards.lookup.clear': 'Clear',
  'gift_cards.lookup.code.label': 'Gift card code',
  'gift_cards.lookup.code.placeholder': '32-hex code',
  'gift_cards.lookup.submit': 'Lookup',
  'gift_cards.lookup.submitting': 'Looking up…',
  'gift_cards.lookup.error.network': 'Network error',
  'gift_cards.list.title': 'Inventory',
  'gift_cards.list.batch.all': 'All batches',
  'gift_cards.list.status.all': 'All statuses',
  'gift_cards.list.account.placeholder': 'Redeemed account',
  'gift_cards.list.column.code': 'Code',
  'gift_cards.list.column.status': 'Status',
  'gift_cards.list.column.amount': 'Amount',
  'gift_cards.list.column.batch': 'Batch',
  'gift_cards.list.column.redeemed_by': 'Redeemed account',
  'gift_cards.list.column.updated': 'Latest state time',
  'gift_cards.list.column.actions': 'Actions',
  'gift_cards.list.column.source': 'Source',
  'gift_cards.list.show_code': 'Show',
  'gift_cards.list.hide_code': 'Hide',
  'gift_cards.list.copy_code': 'Copy',
  'gift_cards.list.empty': 'No gift cards match the filters',
  'gift_cards.list.total': '{total} total',
  'gift_cards.result.code': 'Code',
  'gift_cards.result.face_value': 'Face value',
  'gift_cards.result.issued_at': 'Issued at',
  'gift_cards.result.expires_at': 'Expires at',
  'gift_cards.result.never': 'Never',
  'gift_cards.result.batch_id': 'Batch id',
  'gift_cards.result.batch_id_empty': '—',
  'gift_cards.result.redeemed_at': 'Redeemed at',
  'gift_cards.result.redeemed_by': 'Redeemed by account',
  'gift_cards.result.redeemed_by_empty': '—',
  'gift_cards.result.credit_value': 'USD credit',
  'gift_cards.result.applied_rate': 'Locked rate',
  'gift_cards.result.revoked_at': 'Revoked at',
  'gift_cards.result.revoke': 'Revoke',
  'gift_cards.result.redeem': 'Redeem on behalf',
  'gift_cards.revoke_modal.title': 'Revoke gift card?',
  'gift_cards.revoke_modal.body_prefix': 'This permanently disables ',
  'gift_cards.revoke_modal.body_separator': ' (face value ',
  'gift_cards.revoke_modal.body_suffix': '). Cannot be undone.',
  'gift_cards.revoke_modal.reason.label': 'Reason (optional, recorded in audit log)',
  'gift_cards.revoke_modal.reason.placeholder': 'duplicate / accidental / fraud',
  'gift_cards.revoke_modal.submit': 'Revoke',
  'gift_cards.revoke_modal.submitting': 'Revoking…',
  'gift_cards.revoke_modal.toast': 'Gift card {code} revoked',
  'gift_cards.redeem_modal.title': 'Redeem on behalf',
  'gift_cards.redeem_modal.body_prefix': 'This will credit ',
  'gift_cards.redeem_modal.body_middle': ' to the target account for card ',
  'gift_cards.redeem_modal.body_suffix':
    '. RMB face value: {faceValue}. Funds settle on the platform ledger immediately and cannot be reversed without a balance adjustment.',
  'gift_cards.redeem_modal.account.label': 'Target account id (UUID)',
  'gift_cards.redeem_modal.account.placeholder': 'acct_…',
  'gift_cards.redeem_modal.confirm.label': 'Confirm amount (type {amount})',
  'gift_cards.redeem_modal.reason.label': 'Reason (optional)',
  'gift_cards.redeem_modal.reason.placeholder': 'customer support / promo redemption',
  'gift_cards.redeem_modal.idempotency': 'Idempotency key:',
  'gift_cards.redeem_modal.submit': 'Credit {amount}',
  'gift_cards.redeem_modal.submitting': 'Crediting…',
  'gift_cards.redeem_modal.toast': 'Credited {amount} to {account} — new balance {balance}',
  'gift_cards.redeem_modal.error.account_required': 'Account id is required',
  'gift_cards.redeem_modal.error.amount_mismatch':
    'Confirm amount must equal {amount} (defense-in-depth on a money move)',
  'gift_cards.bulk.title': 'Bulk activity log',
  'gift_cards.bulk.body':
    'No bulk activity tracking in Phase 1. Issued / revoked / redeemed events live on the platform ledger and gateway audit logs; a dedicated activity surface is deferred.',
  'gift_cards.issue.error.amount_positive': 'Amount must be a positive RMB value',
  'gift_cards.issue.error.amount_whole_yuan': 'RMB gift card amount must be whole yuan',
  'gift_cards.issue.error.expires_at_invalid': 'Expires-at must be a valid date',
  'gift_cards.issue.error.expires_at_future': 'Expires-at must be in the future',

  // ── Accounts list ───────────────────────────────────────────────
  'accounts.title': 'Accounts',
  'accounts.subtitle':
    'Tenant accounts with org membership, current balance, and recent ledger access. Click an email to open the detail / balance-adjustment surface.',
  'accounts.search.placeholder': 'Search email…',
  'accounts.search.aria_label': 'Search account email',
  'accounts.has_balance': 'Has balance',
  'accounts.search.submit': 'Search',
  'accounts.search.clear': 'Clear',
  'accounts.error.title': 'Failed to load accounts',
  'accounts.loading_aria': 'Loading accounts…',
  'accounts.empty.filtered.title': 'No accounts match these filters',
  'accounts.empty.filtered.body': 'Clear the filters or refine the search.',
  'accounts.empty.initial.title': 'No accounts registered',
  'accounts.empty.initial.body':
    'Accounts appear here once a tenant signs up through the public API.',
  'accounts.column.email': 'Email',
  'accounts.column.org_id': 'Org id',
  'accounts.column.balance': 'Balance',
  'accounts.column.kind': 'Kind',
  'accounts.column.created': 'Created',
  'accounts.chip.system': 'system',
  'accounts.chip.tenant': 'tenant',
  'accounts.pagination.aria_label': 'Pagination',
  'accounts.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Account detail ──────────────────────────────────────────────
  'account_detail.loading_aria': 'Loading account…',
  'account_detail.error.title': 'Failed to load account',
  'account_detail.back': 'Back to accounts',
  'account_detail.breadcrumb.accounts': 'Accounts',
  'account_detail.meta.created': 'created {iso}',
  'account_detail.meta.org': 'org · {name} ({role})',
  'account_detail.summary.title': 'Summary',
  'account_detail.summary.account_id': 'Account id',
  'account_detail.summary.org_id': 'Org id',
  'account_detail.summary.org_name': 'Org name',
  'account_detail.summary.member_role': 'Member role',
  'account_detail.summary.current_balance': 'Current balance',
  'account_detail.tx.title': 'Recent transactions',
  'account_detail.tx.last_n': 'Last {n} rows',
  'account_detail.tx.empty': 'No ledger activity yet.',
  'account_detail.tx.column.type': 'Type',
  'account_detail.tx.column.amount': 'Amount',
  'account_detail.tx.column.actor': 'Actor',
  'account_detail.tx.column.reason': 'Reason',
  'account_detail.tx.column.created': 'Created',
  'account_detail.adjust.title': 'Balance adjustment',
  'account_detail.adjust.subtitle': 'Signed ledger · negative = debit (allowed)',
  'account_detail.adjust.amount.label': 'Amount (USD)',
  'account_detail.adjust.amount.placeholder': '25 or -12.50',
  'account_detail.adjust.reason.label': 'Reason (required, audit log)',
  'account_detail.adjust.reason.placeholder': 'Customer support / refund / correction…',
  'account_detail.adjust.reason.placeholder_refund':
    'e.g. Order ABC-123 chargeback (reason auto-prefixed with refund:)',
  'account_detail.adjust.reason.placeholder_sla_credit':
    'e.g. 2026-Q2 outage compensation (reason auto-prefixed with sla_credit:)',
  'account_detail.adjust.mode.aria_label': 'Adjustment mode',
  'account_detail.adjust.mode.custom': 'Custom',
  'account_detail.adjust.mode.refund': 'Refund',
  'account_detail.adjust.mode.sla_credit': 'SLA credit',
  'account_detail.adjust.mode.custom.hint':
    'Any signed amount + free-form reason. Negative (debit) allowed.',
  'account_detail.adjust.mode.refund.hint':
    'Positive amount only; reason auto-prefixed with `refund:` so finance can group them at monthly close.',
  'account_detail.adjust.mode.sla_credit.hint':
    'Positive amount only; reason auto-prefixed with `sla_credit:` for SLA incident review.',
  'account_detail.adjust.error.positive_required':
    'Refund / SLA credit must be a positive amount; switch to Custom for debits.',
  'account_detail.adjust.submit': 'Adjust balance',
  'account_detail.adjust.error.amount_required': 'Amount is required',
  'account_detail.adjust.error.amount_non_zero':
    'Amount must be a non-zero USD value (use `-` prefix for debit)',
  'account_detail.adjust.error.reason_required':
    'Reason is required — it lands in the ledger audit log',
  'account_detail.confirm.title': 'Confirm balance adjustment',
  'account_detail.confirm.target_account': 'Target account',
  'account_detail.confirm.current_balance': 'Current balance',
  'account_detail.confirm.adjustment': 'Adjustment',
  'account_detail.confirm.resulting_balance': 'Resulting balance',
  'account_detail.confirm.type_to_confirm': 'Type the amount exactly to confirm ({raw})',
  'account_detail.confirm.idempotency_key': 'Idempotency key:',
  'account_detail.confirm.submitting': 'Submitting…',
  'account_detail.confirm.submit': 'Apply {signed}',
  'account_detail.email_verification.title': 'Email verification',
  'account_detail.email_verification.body':
    'Support actions are audited. Resend first; mark verified only after manual proof.',
  'account_detail.email_verification.status.verified': 'Verified',
  'account_detail.email_verification.status.unverified': 'Unverified',
  'account_detail.email_verification.resend': 'Resend verification',
  'account_detail.email_verification.resending': 'Resending…',
  'account_detail.email_verification.reason_label': 'Manual verify reason',
  'account_detail.email_verification.reason_placeholder': 'Ticket / user proof',
  'account_detail.email_verification.mark_verified': 'Mark verified',
  'account_detail.email_verification.marking': 'Marking…',
  'account_detail.email_verification.support_link': 'Support requests',
  'account_detail.email_verification.toast.resent': 'Verification email resent',
  'account_detail.email_verification.toast.marked': 'Email marked verified',
  'account_detail.email_verification.error.reason_required':
    'Enter a reason before marking verified',

  // ── Account detail / API keys card (Epic #109 / P0.2) ───────────
  'account_detail.api_keys.title': 'API keys',
  'account_detail.api_keys.subtitle': 'All keys for this org (active + revoked)',
  'account_detail.api_keys.loading': 'Loading keys…',
  'account_detail.api_keys.load_error': 'Failed to load keys',
  'account_detail.api_keys.empty': 'This org has not created any API keys yet.',
  'account_detail.api_keys.col.name': 'Name',
  'account_detail.api_keys.col.prefix': 'Prefix',
  'account_detail.api_keys.col.created_at': 'Created',
  'account_detail.api_keys.col.last_used': 'Last used',
  'account_detail.api_keys.col.status': 'Status',
  'account_detail.api_keys.col.actions': 'Actions',
  'account_detail.api_keys.unnamed': '(unnamed)',
  'account_detail.api_keys.status.active': 'Active',
  'account_detail.api_keys.status.revoked': 'Revoked',
  'account_detail.api_keys.revoke': 'Revoke',
  'account_detail.api_keys.revoked_meta': 'Revoked at {iso}',
  'account_detail.api_keys.revoke_modal.title': 'Revoke {name}?',
  'account_detail.api_keys.revoke_modal.body':
    'Revoking invalidates this key immediately; tenant calls using it will get 401. This is irreversible — the tenant must create a new key to restore access.',
  'account_detail.api_keys.revoke_modal.reason.label':
    'Reason (required, written to the audit log)',
  'account_detail.api_keys.revoke_modal.reason.placeholder':
    'Suspected leak / abuse / tenant request…',
  'account_detail.api_keys.revoke_modal.submit': 'Revoke key',
  'account_detail.api_keys.revoke_modal.submitting': 'Revoking…',
  'account_detail.api_keys.toast.revoked': 'Key {id} revoked',
  'account_detail.api_keys.toast.already_revoked': 'Key {id} was already revoked',

  // ── Failed deductions ───────────────────────────────────────────
  'failed_deductions.title': 'Failed deductions',
  'failed_deductions.subtitle':
    'Review debits that failed to settle and resolve the audit trail. Phase 1 does not re-charge — resolutions are recorded for the ledger only.',
  'failed_deductions.filter.unresolved': 'Unresolved',
  'failed_deductions.filter.unresolved.hint': 'awaiting resolution',
  'failed_deductions.filter.resolved': 'Resolved',
  'failed_deductions.filter.resolved.hint': 'resolutions recorded',
  'failed_deductions.filter.all': 'All',
  'failed_deductions.filter.all.hint': 'entire history',
  'failed_deductions.error.title': 'Failed to load deductions',
  'failed_deductions.loading_aria': 'Loading failed deductions…',
  'failed_deductions.empty.unresolved': 'All clear — no unresolved debit failures',
  'failed_deductions.empty.resolved': 'No resolved rows yet',
  'failed_deductions.empty.all': 'No failed deductions recorded',
  'failed_deductions.empty.hint':
    'New rows land here when an account balance goes negative during a billing cycle.',
  'failed_deductions.column.account': 'Account',
  'failed_deductions.column.request_id': 'Request id',
  'failed_deductions.column.amount': 'Amount',
  'failed_deductions.column.reason': 'Reason',
  'failed_deductions.column.created': 'Created',
  'failed_deductions.column.resolved': 'Resolved',
  'failed_deductions.column.actions': 'Actions',
  'failed_deductions.row.resolve': 'Resolve',
  'failed_deductions.row.closed': 'Closed',
  'failed_deductions.resolve_modal.title': 'Resolve failed deduction',
  'failed_deductions.resolve_modal.body_prefix': 'Account ',
  'failed_deductions.resolve_modal.body_separator': ' · amount ',
  'failed_deductions.resolve_modal.body_suffix':
    '. Phase-1 resolution is audit-only — no charge is attempted.',
  'failed_deductions.resolve_modal.legend': 'Resolution',
  'failed_deductions.resolve_modal.forgive': 'Forgive',
  'failed_deductions.resolve_modal.chargeback': 'Chargeback',
  'failed_deductions.resolve_modal.retry': 'Retry',
  'failed_deductions.resolve_modal.forgive.hint':
    'Forgive — write off the negative balance without re-charging',
  'failed_deductions.resolve_modal.chargeback.hint':
    'Chargeback — record that the tenant disputed and do not re-charge',
  'failed_deductions.resolve_modal.retry.hint':
    'Retry — note that the debit was manually re-attempted',
  'failed_deductions.resolve_modal.submitting': 'Resolving…',
  'failed_deductions.resolve_modal.submit': 'Record {choice}',
  'billing_anomalies.title': 'Billing anomalies',
  'billing_anomalies.subtitle':
    'Requests that reached the tenant successfully but still need billing reconciliation or manual follow-up.',
  'billing_anomalies.filter.unresolved': 'Unresolved',
  'billing_anomalies.filter.unresolved.hint': 'still needs settlement',
  'billing_anomalies.filter.resolved': 'Resolved',
  'billing_anomalies.filter.resolved.hint': 'already reconciled or escalated',
  'billing_anomalies.filter.all': 'All',
  'billing_anomalies.filter.all.hint': 'entire anomaly history',
  'billing_anomalies.error.title': 'Failed to load billing anomalies',
  'billing_anomalies.empty.unresolved': 'No unresolved billing anomalies',
  'billing_anomalies.empty.resolved': 'No resolved billing anomalies yet',
  'billing_anomalies.empty.all': 'No billing anomalies recorded',
  'billing_anomalies.empty.hint':
    'Healthy traffic should settle into charged or zero-cost without entering this queue.',
  'billing_anomalies.column.account': 'Account',
  'billing_anomalies.column.request_id': 'Request id',
  'billing_anomalies.column.model': 'Model',
  'billing_anomalies.column.amount': 'Amount',
  'billing_anomalies.column.reason': 'Reason',
  'billing_anomalies.column.replay': 'Replay',
  'billing_anomalies.column.created': 'Created',
  'billing_anomalies.column.resolved': 'Resolved',
  'billing_anomalies.pagination.summary': 'Showing {shown} of {total}',

  // ── Payments orders ─────────────────────────────────────────────
  'payments_orders.title': 'Payment Orders',
  'payments_orders.subtitle':
    "Inspect tenant top-ups and reconcile orders whose ZPay notify didn't land.",
  'payments_orders.status.all': 'All',
  'payments_orders.status.pending': 'Pending',
  'payments_orders.status.paid': 'Paid',
  'payments_orders.status.expired': 'Expired',
  'payments_orders.status.failed': 'Failed',
  'payments_orders.status.refunded': 'Refunded',
  'payments_orders.filter.account_id': 'Account id',
  'payments_orders.filter.from': 'From (ISO-8601)',
  'payments_orders.filter.to': 'To (ISO-8601)',
  'payments_orders.loading_aria': 'Loading payment orders…',
  'payments_orders.error.title': 'Failed to load payment orders',
  'payments_orders.empty': 'No payment orders match the current filters.',
  'payments_orders.column.created': 'Created',
  'payments_orders.column.account': 'Account',
  'payments_orders.column.status': 'Status',
  'payments_orders.column.usd': 'USD',
  'payments_orders.column.cny': 'CNY',
  'payments_orders.column.rate': 'Rate',
  'payments_orders.column.pay': 'Pay',
  'payments_orders.column.paid_at': 'Paid at',
  'payments_orders.column.order_id': 'Order id',
  'payments_orders.column.actions': 'Actions',
  'payments_orders.row.details': 'Details',
  'payments_orders.row.collapse': 'Collapse',
  'payments_orders.row.reconcile': 'Reconcile',
  'payments_orders.showing': 'Showing {n} order(s)',
  'payments_orders.load_more': 'Load more',
  'payments_orders.loading_more': 'Loading…',
  'payments_orders.reconcile_modal.title': 'Reconcile payment order',
  'payments_orders.filter.status_label': 'Status',
  'payments_orders.filter.status_aria_label': 'Filter by status',
  'payments_orders.filter.account_id.placeholder': 'acc_...',
  'payments_orders.filter.to.placeholder': '(no end)',
  'payments_orders.row.reconcile_tooltip.available':
    'Force a ZPay reconciliation; credits immediately if the query succeeds',
  'payments_orders.row.reconcile_tooltip.disabled': 'Reconcile only applies to pending orders',
  'payments_orders.reconcile_modal.body':
    "We'll ask ZPay whether order {orderId} is paid. If yes, we credit {amount} atomically; if no, nothing changes.",
  'payments_orders.reconcile_modal.account': 'Account',
  'payments_orders.reconcile_modal.usd': 'USD',
  'payments_orders.reconcile_modal.cny': 'CNY',
  'payments_orders.reconcile_modal.applied_rate': 'Applied rate',
  'payments_orders.reconcile_modal.note.label': 'Note (optional, audited)',
  'payments_orders.reconcile_modal.note.placeholder':
    'e.g. Customer confirmed ZPay charge but never received notify',
  'payments_orders.reconcile_modal.type_to_confirm': 'Type the order id to confirm',
  'payments_orders.reconcile_modal.type_hint_prefix': 'Paste or type ',
  'payments_orders.reconcile_modal.type_hint_suffix': ' exactly to enable the Reconcile button.',
  'payments_orders.reconcile_modal.submitting': 'Reconciling…',
  'payments_orders.reconcile_modal.submit': 'Reconcile now',
  'payments_orders.reconcile_modal.detail.order_id': 'Order id',
  'payments_orders.reconcile_modal.detail.out_trade_no': 'Out trade no',
  'payments_orders.reconcile_modal.detail.upstream_trade_no': 'Upstream trade no',
  'payments_orders.reconcile_modal.detail.expired_at': 'Expires at',
  'payments_orders.reconcile_modal.detail.empty': '—',
  'payments_orders.reconcile_modal.toast.credited': 'Order {orderId} settled (status={status})',
  'payments_orders.reconcile_modal.toast.upstream':
    'Upstream reports status={status}; order {orderId} not credited',
  'payments_orders.reconcile_modal.toast.already':
    'Order {orderId} already in status={status}; no action taken',

  // ── Payment providers ───────────────────────────────────────────
  'payment_providers.title': 'Payment Providers',
  'payment_providers.subtitle':
    'Manage credentials and endpoint URLs for each payment aggregator. Changes take effect within 60 seconds — platform-api picks up the latest version automatically after rotation.',
  'payment_providers.new': 'New Provider',
  'payment_providers.banner':
    'Changes take effect within 60 seconds — platform-api picks up the latest version automatically after rotation, no redeploy required.',
  'payment_providers.empty.title': 'No payment providers configured yet.',
  'payment_providers.empty.body':
    'Add your first provider to unlock tenant topup. Until then, /v1/billing/topup returns 503.',
  'payment_providers.empty.cta': 'Add Provider',
  'payment_providers.row.rotate': 'Rotate',
  'payment_providers.row.pid': 'PID',
  'payment_providers.row.reveal': 'Reveal',
  'payment_providers.row.revealing': 'Revealing…',
  'payment_providers.row.revealed': 'Revealed',
  'payment_providers.chip.active': 'Active',
  'payment_providers.chip.retired': 'Retired',
  'payment_providers.create.title': 'Add Provider',
  'payment_providers.create.provider': 'Provider',
  'payment_providers.create.pid': 'Merchant ID (PID)',
  'payment_providers.create.secret': 'Secret Key',
  'payment_providers.create.submit_url': 'Submit URL',
  'payment_providers.create.mapi_url': 'MAPI URL',
  'payment_providers.create.query_url': 'Query URL',
  'payment_providers.create.note': 'Note (optional, audited)',
  'payment_providers.create.active_checkbox': 'Make this the active credential for {provider}',
  'payment_providers.create.rotation_warning':
    'An active credential already exists for this provider — adding a new one rotates and retires the prior version.',
  'payment_providers.create.error.pid_required': 'PID is required',
  'payment_providers.create.error.secret_required': 'Secret key is required',
  'payment_providers.loading_aria': 'Loading payment providers…',
  'payment_providers.error.title': 'Failed to load payment providers',
  'payment_providers.create.modal.title.new': 'New {provider} provider',
  'payment_providers.create.modal.title.rotate': 'Rotate {provider} credentials',
  'payment_providers.create.secret.hint':
    'Used for MD5 signing. Plaintext in D1 is intentional — ops self-serves rotation without a deploy.',
  'payment_providers.create.mapi_url.optional_suffix': '(optional)',
  'payment_providers.create.active_hint.rotation':
    'This will retire the current active record. Type-to-confirm required.',
  'payment_providers.create.active_hint.retired':
    'Leaves the current active record in place — this row lands Retired.',
  'payment_providers.create.active_hint.first':
    'No prior active record exists; this row becomes the first active.',
  'payment_providers.create.error.submit_url_required': 'Submit URL is required',
  'payment_providers.create.error.query_url_required': 'Query URL is required',
  'payment_providers.create.error.mapi_url_required_zpay': 'MAPI URL is required for zpay',
  'payment_providers.create.submit.save': 'Save',
  'payment_providers.create.submit.continue': 'Continue',
  'payment_providers.confirm.title.rotation': 'Confirm {provider} credential rotation',
  'payment_providers.confirm.title.save': 'Save {provider} credentials',
  'payment_providers.confirm.body.rotation':
    'You are about to replace the live credentials. Subsequent signing calls will use the new PID + Key within 60 seconds.',
  'payment_providers.confirm.body.save':
    'This record will be saved without affecting the currently-active credential.',
  'payment_providers.confirm.type_prefix': 'Type ',
  'payment_providers.confirm.type_suffix': ' to confirm',
  'payment_providers.confirm.back': 'Back',
  'payment_providers.confirm.submit.saving': 'Saving…',
  'payment_providers.confirm.submit.rotate': 'Rotate Credentials',
  'payment_providers.confirm.submit.save': 'Save',
  'payment_providers.confirm.error.type_mismatch':
    'Type {provider} exactly to confirm the rotation',
  'payment_providers.toast.rotated': 'Rotated {provider} credentials (retired {retiredId})',
  'payment_providers.toast.saved': 'Saved {provider} credentials ({status})',
  'payment_providers.toast.status.active': 'active',
  'payment_providers.toast.status.retired': 'retired',

  // ── Pricing (exchange rate policy) ──────────────────────────────
  'pricing.title': 'Exchange Rate Policy',
  'pricing.subtitle':
    'Topup conversion rate (R) — the CNY price a customer pays to receive $1 USD balance.',
  'pricing.policy.title': 'Current exchange rate policy',
  'pricing.policy.empty': 'No policy set yet. Use the form below to establish the initial rate.',
  'pricing.policy.pair': 'Pair',
  'pricing.policy.current_r': 'Current R',
  'pricing.policy.updated_by': 'Updated by',
  'pricing.policy.updated_at': 'Updated at',
  'pricing.policy.meaning.title': 'What this means',
  'pricing.policy.meaning.body': 'Customer pays ¥{rate} to receive $1.00 USD balance.',
  'pricing.market.title': 'Market reference',
  'pricing.market.subtitle': 'Updated via cron every 6h',
  'pricing.market.empty':
    'No market samples observed yet — cron backfills this card once it ships.',
  'pricing.market.latest_rate': 'Latest market rate',
  'pricing.market.source': 'Source',
  'pricing.market.sampled_at': 'Sampled at',
  'pricing.market.spread': 'Spread vs market',
  'pricing.edit.title': 'Edit rate',
  'pricing.edit.subtitle': 'Type-to-confirm · Audited',
  'pricing.edit.new_r': 'New R (CNY per USD)',
  'pricing.edit.note': 'Note (optional, audited)',
  'pricing.edit.submit': 'Save',
  'pricing.confirm.title': 'Confirm exchange rate change',
  'pricing.confirm.previous': 'Previous rate',
  'pricing.confirm.new': 'New rate',
  'pricing.confirm.market': 'Market reference',
  'pricing.confirm.projected_spread': 'Projected spread',
  'pricing.confirm.note': 'Note',
  'pricing.loading_aria': 'Loading pricing policy…',
  'pricing.error.title': 'Failed to load pricing policy',
  'pricing.policy.note_prefix': 'Note:',
  'pricing.edit.rate_placeholder': '7.89',
  'pricing.edit.note_placeholder': 'Aligning with market move',
  'pricing.edit.error.required': 'New rate is required',
  'pricing.edit.error.not_number': 'New rate must be a number',
  'pricing.edit.error.out_of_range': 'New rate must be in ({min}, {max}] — check the decimal point',
  'pricing.confirm.type_hint': 'Type the new rate exactly to confirm ({rate})',
  'pricing.confirm.error.type_mismatch': 'Type the new rate exactly ({rate}) to confirm',
  'pricing.confirm.submit.saving': 'Saving…',
  'pricing.confirm.submit.save': 'Save {rate}',
  'pricing.toast.updated': 'Exchange rate updated to {rate}',

  // ── Revenue ─────────────────────────────────────────────────────
  'revenue.title': 'Revenue',
  'revenue.subtitle':
    'L1 (topup exchange-rate premium) + L2 (token-sales gross margin) across every tenant and model.',
  'revenue.window.today': 'Today',
  'revenue.window.7d': 'Past 7 days',
  'revenue.window.30d': 'Past 30 days',
  'revenue.summary.title': 'Summary',
  'revenue.summary.subtitle': 'L1 premium + L2 gross margin for the selected window.',
  'revenue.summary.topup_gmv': 'Topup GMV',
  'revenue.summary.usage_gmv': 'Usage GMV',
  'revenue.summary.l1_profit': 'L1 profit',
  'revenue.summary.channel_fees': 'Channel fees',
  'revenue.summary.usd_credited': 'USD credited',
  'revenue.summary.l2_profit': 'L2 profit',
  'revenue.summary.upstream_cost': 'Upstream cost',
  'revenue.summary.total': 'Total gross margin',
  'revenue.daily.title': 'Daily trend',
  'revenue.daily.subtitle': 'Stacked L1 (primary) + L2 (tertiary) profit per day.',
  'revenue.l2.title': 'L2 by model',
  'revenue.l2.subtitle': 'Per-model gross margin on usage. Lossy models flagged automatically.',
  'revenue.l2.currency.label': 'Currency',
  'revenue.l2.currency.aria': 'L2 by model display currency',
  'revenue.l2.column.model': 'Model',
  'revenue.l2.column.usage': 'Usage',
  'revenue.l2.column.usd_charged': 'USD charged',
  'revenue.l2.column.cny_cost': 'CNY cost',
  'revenue.l2.column.charged': 'Charged ({currency})',
  'revenue.l2.column.cost': 'Cost ({currency})',
  'revenue.l2.column.l2_profit': 'L2 profit',
  'revenue.l2.column.l2_pct': 'L2 %',
  'revenue.l2.flag.loss': 'LOSS',
  'revenue.l2.flag.thin': 'THIN',
  'revenue.top_tenants.title': 'Top tenants',
  'revenue.top_tenants.subtitle':
    'Lifetime L1 + L2 contribution per tenant. Sorted by total CNY margin, top {n}.',
  'revenue.top_tenants.column.email': 'Email',
  'revenue.top_tenants.column.topups': 'Topups',
  'revenue.top_tenants.column.l1_profit': 'L1 profit',
  'revenue.top_tenants.column.usage': 'Usage',
  'revenue.top_tenants.column.l2_profit': 'L2 profit',
  'revenue.top_tenants.column.total': 'Total',
  'revenue.summary.window_aria': 'Summary window',
  'revenue.summary.error_label': 'Failed to load summary',
  'revenue.summary.topup.orders': '{n} orders',
  'revenue.summary.usage.api_calls': '{n} API calls',
  'revenue.daily.subtitle_full':
    'Stacked L1 (primary) + L2 (tertiary) profit per day. UTC-bucketed. {days} days.',
  'revenue.daily.error_label': 'Failed to load daily trend',
  'revenue.daily.empty': 'No revenue data yet',
  'revenue.daily.aria_label': 'Daily revenue trend — {days} days',
  'revenue.daily.tooltip': '{date} — L1 {l1} ({topupCount} topups), L2 {l2} ({usageCount} usage)',
  'revenue.l2.error_label': 'Failed to load per-model breakdown',
  'revenue.l2.empty': 'No usage recorded in this window.',
  'revenue.top_tenants.error_label': 'Failed to load top-tenants',
  'revenue.top_tenants.empty': 'No tenants have contributed margin yet.',
  'revenue.top_tenants.no_email': '(no email)',

  // ── Audit log ───────────────────────────────────────────────────
  'audit.title': 'Audit log',
  'audit.subtitle':
    'Append-only trail of every ops-admin mutation. Ordered by `when` descending server-side; filter fields are exact-match only.',
  'audit.filter.action': 'Action',
  'audit.filter.target_type': 'Target type',
  'audit.filter.target_id': 'Target id',
  'audit.filter.ops_user_id': 'Ops user id',
  'audit.filter.target_type.any': 'Any target type',
  'audit.filter.target_type.account': 'account',
  'audit.filter.target_type.gift_card': 'gift-card',
  'audit.filter.target_type.gift_card_batch': 'gift-card-batch',
  'audit.filter.target_type.upstream': 'upstream',
  'audit.filter.target_type.upstream_model_mapping': 'upstream-model-mapping',
  'audit.filter.target_type.upstream_funding_batch': 'upstream-funding-batch',
  'audit.filter.target_type.model': 'model',
  'audit.filter.target_type.failed_deduction': 'failed-deduction',
  'audit.filter.target_type.exchange_rate_policy': 'exchange-rate-policy',
  'audit.filter.target_type.payment_order': 'payment-order',
  'audit.filter.target_type.payment_provider': 'payment-provider',
  'audit.filter.apply': 'Apply',
  'audit.filter.reset': 'Reset',
  'audit.filter.action.placeholder': 'e.g. balance.adjust',
  'audit.filter.ops_user_id.placeholder': 'ops_xxx',
  'audit.filter.target_id.placeholder': 'exact match',
  'audit.error.title': 'Failed to load audit log',
  'audit.loading_aria': 'Loading audit log…',
  'audit.empty.title': 'No audit rows match',
  'audit.empty.body': 'Try widening the filters or clearing them entirely.',
  'audit.column.when': 'When',
  'audit.column.action': 'Action',
  'audit.column.target_type': 'Target type',
  'audit.column.target_id': 'Target id',
  'audit.column.ops_user': 'Ops user',
  'audit.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Upstream detail tabs + summary stats ────────────────────────────
  'upstream_detail.tab.config': 'Configuration',
  'upstream_detail.tab.mappings': 'Model Routing',
  'upstream_detail.tab.funding': 'Funding',
  'upstream_detail.tab.probe': 'Probe',
  'upstream_detail.summary.source': 'Support Source',
  'upstream_detail.summary.endpoint': 'Endpoint',
  'upstream_detail.summary.accounts': 'Accounts',
  'upstream_detail.summary.accounts_unit': 'accounts',
  'upstream_detail.summary.mappings': 'Model Mappings',
  'upstream_detail.summary.mappings_unit': 'routes',
  'upstream_detail.summary.funding': 'Funding',
  'upstream_detail.summary.funding_remaining': 'remaining',
  'upstream_detail.summary.funding_none': 'No funding',

  // ── Upstream maintenance toggle (P2.11) ────────────────────────────
  'upstream_detail.maintenance.label': 'Operational state',
  'upstream_detail.maintenance.state.enabled': 'Routing traffic',
  'upstream_detail.maintenance.state.disabled': 'In maintenance · gateway has stopped routing',
  'upstream_detail.maintenance.enter.button': 'Enter maintenance',
  'upstream_detail.maintenance.resume.button': 'Resume routing',
  'upstream_detail.maintenance.modal.title_enter': 'Put {name} into maintenance?',
  'upstream_detail.maintenance.modal.title_resume': 'Resume routing to {name}?',
  'upstream_detail.maintenance.modal.body_enter':
    'Takes effect immediately: the gateway selector removes this upstream from the candidate set. In-flight requests continue; new requests route through other candidates. For single-point-of-failure SKUs tenants will 404 (see dashboard alerts).',
  'upstream_detail.maintenance.modal.body_resume':
    'Once resumed, the upstream rejoins the candidate set and the selector allocates traffic by the standard SLA × cost rules.',
  'upstream_detail.maintenance.impact.label': 'Currently mapped routes · {count}',
  'upstream_detail.maintenance.impact.none':
    'No model mappings; maintenance does not affect any SKU right now.',
  'upstream_detail.maintenance.confirm.type_label': 'Type `maintenance` to confirm',
  'upstream_detail.maintenance.confirm.type_mismatch': 'Type maintenance exactly to confirm',
  'upstream_detail.maintenance.confirm.submit_enter': 'Confirm maintenance',
  'upstream_detail.maintenance.confirm.submit_resume': 'Confirm resume',

  // ── Account lifecycle: suspend / delete (P2.10) ────────────────────
  'account_lifecycle.title': 'Account lifecycle',
  'account_lifecycle.status.active': 'Active',
  'account_lifecycle.status.suspended': 'Suspended',
  'account_lifecycle.status.deleted': 'Deleted',
  'account_lifecycle.suspended.banner': 'Suspended since {iso} · reason: {reason}',
  'account_lifecycle.suspended.no_reason': '(not provided)',
  'account_lifecycle.deleted.banner':
    'Soft-deleted at {iso}; tenant API requests return 403 ACCOUNT_DELETED.',
  'account_lifecycle.suspend.reason_label': 'Reason (optional)',
  'account_lifecycle.suspend.reason_placeholder': 'e.g. Abuse report 2026-04-20',
  'account_lifecycle.suspend.reason_hint':
    'Stored in accounts.suspension_reason for later lookup; an ops_audit row is also written.',
  'account_lifecycle.suspend.button': 'Suspend',
  'account_lifecycle.unsuspend.button': 'Unsuspend',
  'account_lifecycle.delete.button': 'Soft-delete',
  'account_lifecycle.confirm.title_suspend': 'Suspend {email}?',
  'account_lifecycle.confirm.body_suspend':
    'Immediately blocks the tenant: /internal/auth/verify-key will return ACCOUNT_SUSPENDED and every new request is refused. Historical usage_logs and balances are preserved.',
  'account_lifecycle.confirm.title_unsuspend': 'Unsuspend {email}?',
  'account_lifecycle.confirm.body_unsuspend':
    'Clears suspended_at + suspension_reason; the tenant resumes normal routing immediately.',
  'account_lifecycle.confirm.title_delete': 'Soft-delete {email}?',
  'account_lifecycle.confirm.body_delete':
    'Sets deleted_at; tenant API requests return 403 ACCOUNT_DELETED from now on. Historical usage_logs / balance_transactions stay in D1 (needed for reconciliation). Full GDPR erasure (wiping email + password_hash) is a follow-up task.',
  'account_lifecycle.confirm.type_email': 'Type {email} to confirm',
  'account_lifecycle.confirm.type_mismatch': 'Type {email} exactly to confirm',
  'account_lifecycle.confirm.reason_preview': 'Reason preview: {reason}',
  'account_lifecycle.confirm.submit_suspend': 'Confirm suspend',
  'account_lifecycle.confirm.submit_unsuspend': 'Confirm unsuspend',
  'account_lifecycle.confirm.submit_delete': 'Confirm soft-delete',
  'account_lifecycle.toast.suspended': '{email} suspended',
  'account_lifecycle.toast.unsuspended': '{email} unsuspended',
  'account_lifecycle.toast.deleted': '{email} soft-deleted',

  // ── Org routing policy (P2.9) ──────────────────────────────────────
  'routing_policy.title': 'Org routing policy',
  'routing_policy.body':
    'Applies to every tenant account under org `{org}`. Governs the SLA×cost selector. Blank = default; note that an explicit 0 is different from unset. Gateway D1 reads this directly, so edits take effect within seconds of save.',
  'routing_policy.field.allowlist': 'Upstream allowlist',
  'routing_policy.field.allowlist.placeholder': 'ups_primary, ups_backup',
  'routing_policy.field.allowlist.hint':
    'Comma-separated; only these upstreams are eligible. Empty = no allowlist filter.',
  'routing_policy.field.denylist': 'Upstream denylist',
  'routing_policy.field.denylist.placeholder': 'ups_retired',
  'routing_policy.field.denylist.hint':
    'Comma-separated; these upstreams are removed from the candidate set.',
  'routing_policy.field.sla_weight': 'SLA weight (0–1)',
  'routing_policy.field.cost_weight': 'Cost weight (0–1)',
  'routing_policy.field.weight.placeholder': 'blank = default',
  'routing_policy.field.min_availability_pct': 'Min availability %',
  'routing_policy.field.max_p95_latency_ms': 'Max p95 latency (ms)',
  'routing_policy.field.max_p95.placeholder': 'blank = no cap',
  'routing_policy.empty': 'No policy row yet; global defaults apply.',
  'routing_policy.updated_at': 'Last updated: {iso}',
  'routing_policy.save': 'Save',
  'routing_policy.clear': 'Clear (revert to defaults)',
  'routing_policy.clear.pending': 'Clearing…',
  'routing_policy.error.weight_range': 'Weights must be in [0, 1]; blank falls back to defaults.',
  'routing_policy.error.pct_range': 'Min availability must be in [0, 100].',
  'routing_policy.error.latency_integer':
    'Max p95 latency must be a non-negative integer (ms); blank disables the cap.',
  'routing_policy.toast.saved': 'Routing policy for org `{org}` saved',
  'routing_policy.toast.cleared': 'Routing policy for org `{org}` cleared — defaults apply',

  // ── Upstream onboarding wizard (P1.5) ─────────────────────────────
  'upstream_wizard.title': 'New upstream onboarding',
  'upstream_wizard.subtitle':
    'Four steps take an upstream from zero to routable: basics → model routes → register funding → probe. Each step writes to D1 immediately, so walking away mid-flow does not lock anything up.',
  'upstream_wizard.breadcrumb.upstreams': 'Upstreams',
  'upstream_wizard.breadcrumb.new': 'New',
  'upstream_wizard.progress.aria_label': 'Onboarding progress',
  'upstream_wizard.back': 'Back',
  'upstream_wizard.next': 'Next',
  'upstream_wizard.step.basic.title': 'Basics',
  'upstream_wizard.step.basic.heading': 'Create the upstream record',
  'upstream_wizard.step.basic.body':
    'Writes a `upstream_configs` row and stores the API secret. `enabled` stays `false` until the last step flips it.',
  'upstream_wizard.step.basic.name': 'Display name',
  'upstream_wizard.step.basic.name.placeholder': 'anthropic-direct-a',
  'upstream_wizard.step.basic.endpoint': 'Endpoint URL',
  'upstream_wizard.step.basic.endpoint.placeholder': 'https://api.anthropic.com',
  'upstream_wizard.step.basic.priority': 'Priority',
  'upstream_wizard.step.basic.visibility': 'Tenant visibility',
  'upstream_wizard.step.basic.cookie_passthrough': 'Cookie passthrough',
  'upstream_wizard.step.basic.cookie_passthrough.body':
    'Allow tenant cookies to pass through to this upstream.',
  'upstream_wizard.step.basic.cookie_passthrough.hint':
    'Default stays off. Only enable if this upstream truly requires tenant cookies.',
  'upstream_wizard.step.basic.submit': 'Create upstream',
  'upstream_wizard.step.basic.submit.pending': 'Creating…',
  'upstream_wizard.step.basic.error.required': 'Name and endpoint are required',
  'upstream_wizard.step.basic.error.priority': 'Priority must be a non-negative integer',
  'upstream_wizard.step.keys.title': 'Add keys',
  'upstream_wizard.step.keys.heading': 'Add API keys for {name}',
  'upstream_wizard.step.keys.body':
    'Add at least one key bucket. The gateway round-robins enabled buckets when forwarding requests.',
  'upstream_wizard.step.keys.count': '{count} key bucket(s) added',
  'upstream_wizard.step.keys.label': 'Label',
  'upstream_wizard.step.keys.label.placeholder': 'e.g. primary-key',
  'upstream_wizard.step.keys.add': 'Add',
  'upstream_wizard.step.keys.toast.added': 'Key bucket added',
  'upstream_wizard.step.keys.error.min_one': 'Add at least one key bucket to continue',
  'upstream_wizard.step.keys.error.secret_required': 'Enter an API key',
  'upstream_wizard.step.models.title': 'Connect models',
  'upstream_wizard.step.models.heading': 'Connect model routing for {name}',
  'upstream_wizard.step.models.body':
    'Select vendor, channel, and model to create upstream routing mappings. Add at least one to continue.',
  'upstream_wizard.step.models.add': 'Add mapping',
  'upstream_wizard.step.models.toast.added': 'Model mapping added',
  'upstream_wizard.step.models.error.min_one': 'Add at least one model mapping to continue',
  'upstream_wizard.step.probe.title': 'Probe',
  'upstream_wizard.step.probe.heading': 'Probe {name}',
  'upstream_wizard.step.probe.body':
    'Pick a mapped route and run a real request. A failed probe does NOT block enable — but you should investigate first. On success, click Finish to flip enabled=true and open the detail page.',
  'upstream_wizard.step.probe.target.label': 'Probe target',
  'upstream_wizard.step.probe.run': 'Run probe',
  'upstream_wizard.step.probe.run.pending': 'Probing…',
  'upstream_wizard.step.probe.finish': 'Finish + enable',
  'upstream_wizard.step.probe.finish.pending': 'Enabling…',
  'upstream_wizard.step.probe.warning.failed':
    'Probe failed. Confirm before enabling — fix config from the detail page and re-probe.',
  'upstream_wizard.toast.created': 'Upstream {name} created (not yet enabled)',
  'upstream_wizard.toast.attached': '{vendor}/{model} mapped',
  'upstream_wizard.toast.funded': 'Funding batch registered',
  'upstream_wizard.toast.enabled': '{name} is live — gateway will route to it',

  // ── Support: per-tenant request query (P0.4) ─────────────────────
  'support.requests.title': 'Tenant request lookup',
  'support.requests.subtitle':
    'Gateway request log filtered by account, for billing-dispute investigation. Restricted to ops-admin and ops-support roles.',
  'support.requests.filter.account_id': 'Account id',
  'support.requests.filter.account_id.placeholder': 'acc_xxx',
  'support.requests.filter.from': 'From',
  'support.requests.filter.to': 'To',
  'support.requests.filter.request_id': 'Request id (optional, exact match)',
  'support.requests.filter.request_id.placeholder': 'req_xxx',
  'support.requests.filter.apply': 'Apply',
  'support.requests.filter.reset': 'Reset',
  'support.requests.cta.title': 'Enter an account id',
  'support.requests.cta.body':
    'Fill in the target account id above and click Apply. Time range is optional — the default window is the last 24 hours. Setting a request id bypasses the time range and performs an exact lookup.',
  'support.requests.error.title': 'Failed to load request log',
  'support.requests.loading_aria': 'Loading request log…',
  'support.requests.account.account_id': 'Account',
  'support.requests.account.email': 'Email',
  'support.requests.account.org': 'Organization',
  'support.requests.account.org.none': '(no org)',
  'support.requests.empty.title': 'No requests in this range',
  'support.requests.empty.body': 'Widen the time range or clear the request id and try again.',
  'support.requests.column.when': 'When',
  'support.requests.column.request_id': 'Request id',
  'support.requests.column.model': 'Model',
  'support.requests.column.status': 'Status',
  'support.requests.column.latency': 'Latency',
  'support.requests.column.cost': 'Cost',
  'support.requests.column.api_key': 'API key',
  'support.requests.column.upstream': 'Upstream',
  'support.requests.row.api_key.unnamed': '(unnamed)',
  'support.requests.row.upstream.deleted': '(deleted)',
  'support.requests.detail.request_id': 'Request id',
  'support.requests.detail.upstream_id': 'Upstream id',
  'support.requests.detail.protocol': 'Protocol',
  'support.requests.detail.created_at': 'Created at',
  'support.requests.detail.input_tokens': 'Input tokens',
  'support.requests.detail.output_tokens': 'Output tokens',
  'support.requests.detail.cache_read': 'Cache read',
  'support.requests.detail.cache_write': 'Cache write',
  'support.requests.pagination.aria_label': 'Request-log pagination',
  'support.requests.pagination.range': 'Showing {start}–{end} of {total}',

  // ── Probe modal + result panel ──────────────────────────────────
  'probe.outcome.pass': 'Pass',
  'probe.outcome.fail_shape': 'Fail · shape',
  'probe.outcome.fail_content': 'Fail · content',
  'probe.outcome.fail_upstream_error': 'Fail · upstream error',
  'probe.outcome.fail_timeout': 'Fail · timeout',
  'probe.id_label': 'probe {id}…',
  'probe.latency': 'Latency',
  'probe.cost': 'Cost',
  'probe.upstream_status': 'Upstream status',
  'probe.input_output_tokens': 'Input / Output tokens',
  'probe.cache_read_write': 'Cache read / write',
  'probe.charge_skipped': 'Charge skipped',
  'probe.error': 'Error',
  'probe.modal.title': 'Probe upstream',
  'probe.modal.close_aria': 'Close probe dialog',
  'probe.modal.error.vendor_model_required': 'Vendor and model are required',
  'probe.modal.vendor.label': 'Vendor',
  'probe.modal.vendor.placeholder': 'e.g. anthropic',
  'probe.modal.model.label': 'Model',
  'probe.modal.model.placeholder': 'e.g. claude-sonnet-4-5',
  'probe.modal.protocol.label': 'Protocol',
  'probe.modal.submit': 'Run probe',
  'probe.modal.submitting': 'Probing…',

  // ── Reports ─────────────────────────────────────────────────────
  'reports.title': 'Reports',
  'reports.subtitle': 'Compliance + finance reporting. Month-scoped aggregations, CSV exports.',
  'reports.month_label': 'Month',
  'reports.regulatory.title': 'Regulatory extract',
  'reports.regulatory.body':
    'Per-account monthly usage for PRC AI filing. Only accounts with usage in the window are listed.',
  'reports.regulatory.csv_download': 'Download CSV',
  'reports.regulatory.loading': 'Loading regulatory extract…',
  'reports.regulatory.error': 'Failed to load regulatory extract.',
  'reports.regulatory.empty': 'No account usage recorded in this month.',
  'reports.regulatory.row_count': '{count} accounts',
  'reports.regulatory.truncated':
    'Row count exceeded the export cap; some accounts are omitted. Raise the cap or paginate.',
  'reports.regulatory.column.account_id': 'Account id',
  'reports.regulatory.column.email': 'Email',
  'reports.regulatory.column.registered_at': 'Registered at',
  'reports.regulatory.column.input_tokens': 'Input tokens',
  'reports.regulatory.column.output_tokens': 'Output tokens',
  'reports.regulatory.column.cost_usd': 'Cost (USD)',
  'reports.monthly.title': 'Monthly reconciliation',
  'reports.monthly.body':
    'Revenue ledger ↔ payment orders for the selected month. Investigate any drift.',
  'reports.monthly.csv_download': 'Download CSV',
  'reports.monthly.loading': 'Loading monthly report…',
  'reports.monthly.error': 'Failed to load monthly report.',
  'reports.monthly.drift.title': 'Drift detected.',
  'reports.monthly.drift.body':
    'Cash collected differs from recognised topups by {amount}. Investigate missing notify callbacks or ledger writes.',
  'reports.monthly.tile.paid_cny': 'Paid orders (CNY)',
  'reports.monthly.tile.paid_count': '{count} orders',
  'reports.monthly.tile.topups_cny_gross': 'Topups (CNY gross)',
  'reports.monthly.tile.topups_count': '{count} ledger rows',
  'reports.monthly.tile.usage_usd': 'Usage charged (USD)',
  'reports.monthly.tile.usage_rows': '{rows} usage rows',
  'reports.monthly.tile.l2_profit': 'L2 profit (CNY)',
  'reports.monthly.tile.l1_profit': 'L1 profit {amount}',
  'reports.monthly.detail.metric': 'Metric',
  'reports.monthly.detail.value': 'Value',
  'reports.monthly.detail.period_start': 'Period start',
  'reports.monthly.detail.period_end': 'Period end',
  'reports.monthly.detail.paid_cny_net': 'Topups (CNY net of channel fees)',
  'reports.monthly.detail.usd_credited': 'USD credited to balances',
  'reports.monthly.detail.ops_adjust_rows': 'Ops adjustment rows',
  'reports.monthly.detail.ops_adjust_usd': 'Ops adjustment USD',
  'reports.monthly.detail.drift_cny': 'Drift (paid − topups, CNY)',

  // ── Webhook events ──────────────────────────────────────────────
  'webhook_events.title': 'Webhook events',
  'webhook_events.subtitle':
    'Inbox of webhook callbacks across providers. Filter, expand raw payloads, mark stuck events processed.',
  'webhook_events.filter.provider': 'Provider',
  'webhook_events.filter.provider_placeholder': 'e.g. zpay',
  'webhook_events.filter.processed': 'Status',
  'webhook_events.filter.processed.all': 'All',
  'webhook_events.filter.processed.pending': 'Pending',
  'webhook_events.filter.processed.done': 'Processed',
  'webhook_events.filter.since': 'Since (ISO-8601)',
  'webhook_events.loading': 'Loading webhook events…',
  'webhook_events.error': 'Failed to load webhook events.',
  'webhook_events.empty': 'No webhook events match the current filters.',
  'webhook_events.column.created_at': 'Created at',
  'webhook_events.column.provider': 'Provider',
  'webhook_events.column.event_type': 'Event type',
  'webhook_events.column.status': 'Status',
  'webhook_events.column.payload': 'Payload',
  'webhook_events.column.actions': 'Actions',
  'webhook_events.status.processed': 'Processed',
  'webhook_events.status.pending': 'Pending',
  'webhook_events.payload.expand': 'Expand payload',
  'webhook_events.action.mark_processed': 'Mark processed',
  'webhook_events.action.marking': 'Marking…',
  'webhook_events.toast.marked': 'Event marked processed.',

  // ── Cron runs ───────────────────────────────────────────────────
  'cron_runs.title': 'Cron runs',
  'cron_runs.subtitle':
    'History of control-loops ticks. Each row is one scheduled invocation across all due handlers.',
  'cron_runs.filter.handler': 'Handler',
  'cron_runs.filter.handler_placeholder': 'e.g. usage_rollup',
  'cron_runs.filter.since': 'Since (ISO-8601)',
  'cron_runs.filter.since_placeholder': 'YYYY-MM-DDTHH:MM:SSZ',
  'cron_runs.loading': 'Loading cron runs…',
  'cron_runs.error': 'Failed to load cron runs.',
  'cron_runs.empty': 'No cron runs match the current filters.',
  'cron_runs.column.scheduled_at': 'Scheduled at',
  'cron_runs.column.duration': 'Duration',
  'cron_runs.column.due': 'Due',
  'cron_runs.column.ok': 'OK',
  'cron_runs.column.fail': 'Fail',
  'cron_runs.column.outcomes': 'Outcomes',
  'cron_runs.outcomes.expand': 'Expand raw outcomes',

  // ── Ops users ───────────────────────────────────────────────────
  'ops_users.title': 'Ops users',
  'ops_users.subtitle':
    'Admin directory for ops user accounts. Disable compromised users, re-enable legitimate ones, force password reset.',
  'ops_users.loading': 'Loading ops users…',
  'ops_users.error': 'Failed to load ops users.',
  'ops_users.empty': 'No ops users found.',
  'ops_users.column.email': 'Email',
  'ops_users.column.roles': 'Roles',
  'ops_users.column.status': 'Status',
  'ops_users.column.created_at': 'Created at',
  'ops_users.column.actions': 'Actions',
  'ops_users.status.active': 'Active',
  'ops_users.status.disabled': 'Disabled',
  'ops_users.status.no_password': 'needs re-seed',
  'ops_users.action.disable': 'Disable',
  'ops_users.action.disabling': 'Disabling…',
  'ops_users.action.enable': 'Re-enable',
  'ops_users.action.enabling': 'Enabling…',
  'ops_users.action.reset_password': 'Reset password',
  'ops_users.action.resetting': 'Resetting…',
  'ops_users.action.reset_confirm': 'Confirm reset',
  'ops_users.action.cancel': 'Cancel',
  'ops_users.reset.confirm_prompt':
    'Type the email ({email}) to confirm. Reset deletes the credential row and invalidates all live sessions.',
  'ops_users.toast.disabled': 'Ops user disabled.',
  'ops_users.toast.enabled': 'Ops user re-enabled.',
  'ops_users.toast.reset': 'Password reset. {sessions} session(s) invalidated.',

  // ── DR check ───────────────────────────────────────────────────
  'dr_check.title': 'DR readiness drill',
  'dr_check.subtitle':
    'Run an on-demand check against both databases. Probes read + write paths on platform-db, read on gateway-db.',
  'dr_check.runner.title': 'Drill runner',
  'dr_check.runner.body':
    'Click "Run drill" to execute the four-probe sequence. Results display below.',
  'dr_check.action.run': 'Run drill',
  'dr_check.action.running': 'Running…',
  'dr_check.idle': 'No drill results yet. Click "Run drill" to start.',
  'dr_check.toast.pass': 'DR drill: all probes passed.',
  'dr_check.toast.fail': 'DR drill: one or more probes failed.',
  'dr_check.overall.pass_title': 'All probes passed.',
  'dr_check.overall.fail_title': 'One or more probes failed.',
  'dr_check.overall.body': 'Started at {started}, took {duration}ms.',
  'dr_check.column.probe': 'Probe',
  'dr_check.column.outcome': 'Outcome',
  'dr_check.column.latency': 'Latency',
  'dr_check.column.detail': 'Detail',
  'dr_check.probe.pass': 'Pass',
  'dr_check.probe.fail': 'Fail',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': 'Notifications',
  'toast.dismiss_aria': 'Dismiss notification',

  // ── Channels (#134, reworked #143) ─────────────────────────────
  'channels.title': 'Channel Management',
  'channels.subtitle': 'Manage channels and suppliers',
  'channels.create_btn': 'New Channel',
  'channels.search.placeholder': 'Search channel, slug, supplier…',
  'channels.empty': 'No channels yet',
  'channels.col.display_name': 'Channel Name',
  'channels.col.rate_multiplier': 'Rate',
  'channels.col.products': 'Products',
  'channels.col.supplier': 'Supplier',
  'channels.col.official': 'Official',
  'channels.col.created_at': 'Created',
  'channels.row.delete': 'Delete channel',
  'channels.create_modal.title': 'New Channel',
  'channels.create_modal.display_name': 'Display Name',
  'channels.create_modal.display_name_placeholder': 'Enter channel display name',
  'channels.create_modal.supplier_name': 'Supplier',
  'channels.create_modal.supplier_placeholder': 'Supplier name',
  'channels.create_modal.slug_hint': 'unique identifier',
  'channels.create_modal.rate_multiplier': 'Rate Multiplier',
  'channels.create_modal.rate_multiplier_hint':
    'Tenant charge = list price × multiplier. Default 1.0',
  'channels.create_modal.is_official': 'Official channel',
  'channels.create_modal.is_official_hint': 'Mark as officially certified resource',
  'channels.create_modal.cancel': 'Cancel',
  'channels.create_modal.submit': 'Create',
  'channels.create_modal.creating': 'Creating…',
  'channels.toast.created': 'Channel created',
  'channels.toast.deleted': 'Channel deleted',
  'channels.toast.duplicate': 'A channel with this slug already exists',

  // ── Channel Detail (#143) ─────────────────────────────────────
  'channel_detail.not_found': 'Channel not found',
  'channel_detail.vendors_count': 'vendors',
  'channel_detail.models_count': 'models',
  'channel_detail.edit_info': 'Edit Info',
  'channel_detail.add_model': 'Add Model',
  'channel_detail.rate_multiplier': 'Channel Rate',
  'channel_detail.rate_hint': 'Tenant charge = list price × this rate',
  'channel_detail.toast.rate_updated': 'Rate updated',
  'channel_detail.toast.info_updated': 'Channel info updated',
  'channel_detail.empty': 'No models yet. Click "Add Model" to configure channel products.',
  'channel_detail.col.model': 'Model',
  'channel_detail.edit_modal.title': 'Edit Channel Info',
  'channel_detail.edit_modal.save': 'Save',
  'channel_detail.edit_modal.saving': 'Saving…',
  'channel_detail.edit_modal.display_name_required': 'Display name is required',
  'channel_detail.edit_modal.rate_invalid': 'Rate must be a positive number',
  'channel_detail.add_modal.title': 'Add Models to Channel',
  'channel_detail.add_modal.vendor': 'Vendor',
  'channel_detail.add_modal.select_vendor': 'Select vendor…',
  'channel_detail.add_modal.select_all': 'Select all',
  'channel_detail.add_modal.selected': 'selected',
  'channel_detail.add_modal.no_models': 'No models available (all models already in channel)',
  'channel_detail.add_modal.cancel': 'Cancel',
  'channel_detail.add_modal.submit': 'Add',
  'channel_detail.add_modal.adding': 'Adding…',
  'channel_detail.toast.model_added': 'Model added to channel',
  'channel_detail.toast.models_added': 'model(s) added to channel',
  'channel_detail.toast.model_removed': 'Model removed from channel',

  // ── Model Mappings (#134) ──────────────────────────────────────
  'mappings.title': 'Model Mappings',
  'mappings.legacy_title': 'Legacy model mappings (migration reference)',
  'mappings.legacy_subtitle':
    'This table shows existing legacy mapping records only: historical channel SKUs that were manually pointed at an upstream model name. It is not the new hot-path routing configuration.',
  'mappings.legacy.empty': 'No legacy model mappings.',
  'mappings.legacy.col.status': 'Legacy state',
  'mappings.legacy.status.mapped': 'Legacy mapping',
  'mappings.filter.all_vendors': 'All Vendors',
  'mappings.filter.all_channels': 'All Channels',
  'mappings.empty': 'No model mappings yet',
  'mappings.col.vendor': 'Vendor',
  'mappings.col.channel': 'Channel',
  'mappings.col.model': 'Model',
  'mappings.col.upstream_name': 'Upstream Name',
  'mappings.add_modal.title': 'Add Model Mapping',
  'mappings.add_modal.select_vendor': 'Select vendor…',
  'mappings.add_modal.select_channel': 'Select channel…',
  'mappings.add_modal.select_model': 'Select model…',
  'mappings.add_modal.select_protocol': 'Select protocol…',
  'mappings.add_modal.cancel': 'Cancel',
  'mappings.add_modal.submit': 'Add',
  'mappings.add_modal.adding': 'Adding…',
  'mappings.toast.created': 'Model mapping added',
  'mappings.toast.deleted': 'Model mapping deleted',
  'mappings.toast.duplicate': 'This mapping already exists',

  // ── Model Detail — Channel Pricing (#134) ──────────────────────
  'model_detail.channel_pricing.title': 'Channel Pricing',
  'model_detail.channel_pricing.empty': 'No channels for this vendor yet',
  'model_detail.channel_pricing.col_channel': 'Channel',
  'model_detail.channel_pricing.col_multiplier': 'Multiplier',
  'model_detail.channel_pricing.col_input_price': 'Input $/M',
  'model_detail.channel_pricing.col_output_price': 'Output $/M',
  'model_detail.channel_pricing.col_supplier': 'Supplier',

  // ── Vendors (#143) ────────────────────────────────────────────
  'shell.nav.vendors': 'Vendors',
  'vendors.title': 'Vendor Management',
  'vendors.subtitle': 'Manage platform model vendors',
  'vendors.create_btn': 'New Vendor',
  'vendors.search.placeholder': 'Search vendors…',
  'vendors.empty': 'No vendors yet',
  'vendors.col.display_name': 'Vendor Name',
  'vendors.col.created_at': 'Created',
  'vendors.row.delete': 'Delete vendor',
  'vendors.create_modal.title': 'New Vendor',
  'vendors.create_modal.display_name': 'Display Name',
  'vendors.create_modal.display_name_placeholder': 'e.g. Anthropic',
  'vendors.create_modal.cancel': 'Cancel',
  'vendors.create_modal.submit': 'Create',
  'vendors.create_modal.creating': 'Creating…',
  'vendors.toast.created': 'Vendor created',
  'vendors.toast.deleted': 'Vendor deleted',
  'vendors.toast.duplicate': 'A vendor with this slug already exists',

  // ── Upstream Accounts (upstream detail accounts tab) ────────────
  'upstream_detail.tab.accounts': 'Accounts',
  'upstream_accounts.title': 'Supplier Accounts',
  'upstream_accounts.add_btn': 'New Account',
  'upstream_accounts.empty': 'No accounts',
  'upstream_accounts.count': '{count} account(s)',
  'upstream_accounts.add_modal.title': 'New Supplier Account',
  'upstream_accounts.add_modal.label': 'Account Label',
  'upstream_accounts.add_modal.label.placeholder': 'e.g. hellsoul86@gmail.com',
  'upstream_accounts.add_modal.rpm': 'RPM Limit',
  'upstream_accounts.add_modal.rate_multiplier': 'Account Discount Multiplier',
  'upstream_accounts.add_modal.submit': 'Create',
  'upstream_accounts.add_modal.creating': 'Creating…',
  'upstream_accounts.add_modal.cancel': 'Cancel',
  'upstream_accounts.add_modal.error.label_required': 'Account label is required',
  'upstream_accounts.toast.created': 'Account created',
  'upstream_accounts.toast.deleted': 'Account deleted',
  'upstream_accounts.section.keys': 'API Keys',
  'upstream_accounts.channels.none': 'No channel binding (matches all)',
  'upstream_accounts.keys.none': 'No keys for this account',
  'upstream_accounts.keys.add_btn': 'Add Key',
  'upstream_accounts.keys.add_modal.title': 'Add API Key',
  'upstream_accounts.keys.add_modal.auth_secret_ref': 'Secret Key',
  'upstream_accounts.keys.add_modal.auth_secret_ref.placeholder': 'sk-...',
  'upstream_accounts.keys.add_modal.label': 'Label (optional)',
  'upstream_accounts.keys.add_modal.rpm': 'RPM Limit',
  'upstream_accounts.keys.add_modal.submit': 'Create',
  'upstream_accounts.keys.add_modal.creating': 'Creating…',
  'upstream_accounts.keys.add_modal.channels': 'Channel Bindings',
  'upstream_accounts.keys.add_modal.channels_hint': 'Leave empty to match all channels',
  'upstream_accounts.keys.add_modal.error.secret_required': 'Secret key is required',
  'upstream_accounts.keys.toast.created': 'Key created',
  'upstream_accounts.keys.toast.channels_failed': 'Channel binding failed, please set manually',
  'upstream_accounts.keys.toast.deleted': 'Key deleted',
  'upstream_accounts.keys.toast.channels_updated': 'Channel bindings updated',
  'upstream_accounts.keys.edit_channels': 'Edit channel bindings',
  'upstream_accounts.chip.enabled': 'enabled',
  'upstream_accounts.chip.disabled': 'disabled',
  'upstream_accounts.chip.status_label': 'Status',
  'upstream_accounts.rate_multiplier_short': 'Discount',

  'upstream_detail.channel_costs.title': 'Upstream Channel Cost Multipliers',
  'upstream_detail.channel_costs.subtitle':
    'Defaults to the downstream channel sales multiplier until ops sets an upstream-specific cost.',
  'upstream_detail.channel_costs.empty': 'No channel cost multipliers.',
  'upstream_detail.channel_costs.col.channel': 'Channel',
  'upstream_detail.channel_costs.col.vendor': 'Vendor / Protocol',
  'upstream_detail.channel_costs.col.multiplier': 'Cost Multiplier',
  'upstream_detail.channel_costs.multiplier_label': 'Cost Multiplier',
  'upstream_detail.channel_costs.invalid': 'Cost multiplier must be a positive number',
  'upstream_detail.channel_costs.updated': 'Upstream channel cost multiplier updated',

  // ── Key Buckets (#143) ────────────────────────────────────────
  'key_buckets.title': 'API Key Buckets',
  'key_buckets.add_btn': 'Add Key',
  'key_buckets.empty': 'No key buckets yet',
  'key_buckets.col.label': 'Label',
  'key_buckets.col.key_preview': 'Key Preview',
  'key_buckets.col.status': 'Status',
  'key_buckets.enabled': 'Enabled',
  'key_buckets.disabled': 'Disabled',
  'key_buckets.add_modal.title': 'Add API Key Bucket',
  'key_buckets.add_modal.label': 'Label',
  'key_buckets.add_modal.label_placeholder': 'e.g. Production key #1',
  'key_buckets.add_modal.cancel': 'Cancel',
  'key_buckets.add_modal.submit': 'Add',
  'key_buckets.add_modal.creating': 'Adding…',
  'key_buckets.toast.created': 'Key bucket added',
  'key_buckets.toast.deleted': 'Key bucket deleted',
  'key_buckets.confirm_delete': 'Confirm',
};
